import axios from 'axios';

// Debug environment variables
console.log('Admin API Configuration:');
console.log('REACT_APP_ADMIN_API_BASE_URL:', process.env.REACT_APP_ADMIN_API_BASE_URL);
console.log('REACT_APP_DEPLOYMENT_ENV:', process.env.REACT_APP_DEPLOYMENT_ENV);
console.log('NODE_ENV:', process.env.NODE_ENV);

// Get base URL with fallback
const getBaseURL = () => {
  const envBaseURL = process.env.REACT_APP_ADMIN_API_BASE_URL;
  
  // First priority: environment variable
  if (envBaseURL && envBaseURL.trim() !== '') {
    console.log('Using environment API URL:', envBaseURL);
    return envBaseURL.endsWith('/') ? envBaseURL.slice(0, -1) : envBaseURL;
  }
  
  // Second priority: fallback based on deployment environment
  const isDevelopment = process.env.REACT_APP_DEPLOYMENT_ENV === 'development' || 
                       process.env.NODE_ENV === 'development';
  
  const fallbackURL = isDevelopment 
    ? 'https://admin-api-test.any-ship.com'
    : 'https://admin-api.any-ship.com';
  
  console.warn('Environment API URL not set, using fallback:', fallbackURL);
  return fallbackURL;
};

const baseURL = getBaseURL();
console.log('Final API Base URL:', baseURL);

// Create axios instance for admin API
const apiClient = axios.create({
  baseURL: baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to log requests
apiClient.interceptors.request.use(
  (config) => {
    console.log('Making API request to:', config.baseURL + config.url);
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const adminApi = {
  // Settings
  getSettings: async () => {
    return await apiClient.get('/api/admin/settings');
  },

  updateSettings: async (settings) => {
    return await apiClient.put('/api/admin/settings', settings);
  },
  // Authentication
  login: async (username, password) => {
    return await apiClient.post('/api/admin/login', { username, password });
  },

  verifyToken: async () => {
    return await apiClient.get('/api/admin/verify');
  },

  // Customers
  getCustomers: async (params = {}) => {
    return await apiClient.get('/api/admin/customers', { params });
  },

  getCustomerDetails: async (customerId) => {
    return await apiClient.get(`/api/admin/customers/${customerId}`);
  },

  // Orders
  getOrders: async (params = {}) => {
    return await apiClient.get('/api/admin/orders', { params });
  },

  getOrderDetails: async (orderId) => {
    return await apiClient.get(`/api/admin/orders/${orderId}`);
  },

  updateOrderStatus: async (orderId, status) => {
    return await apiClient.patch(`/api/admin/orders/${orderId}/status`, { status });
  },

  refundOrder: async (orderId, amount) => {
    return await apiClient.post(`/api/admin/orders/${orderId}/refund`, { amount });
  },

  // Analytics
  getAnalytics: async (params = {}) => {
    return await apiClient.get('/api/admin/analytics', { params });
  },

  getDashboardStats: async () => {
    return await apiClient.get('/api/admin/dashboard/stats');
  },

  // Shippo Integration
  getShippoTransactions: async (params = {}) => {
    return await apiClient.get('/api/admin/shippo/transactions', { params });
  },

  getShippoTransaction: async (transactionId) => {
    return await apiClient.get(`/api/admin/shippo/transactions/${transactionId}`);
  },

  // Stripe Integration
  getStripeCustomers: async (params = {}) => {
    return await apiClient.get('/api/admin/stripe/customers', { params });
  },

  getStripePayments: async (params = {}) => {
    return await apiClient.get('/api/admin/stripe/payments', { params });
  },

  getStripePayment: async (paymentId) => {
    return await apiClient.get(`/api/admin/stripe/payments/${paymentId}`);
  },

  // Export
  exportCustomers: async (params = {}) => {
    return await apiClient.get('/api/admin/export/customers', { 
      params,
      responseType: 'blob'
    });
  },

  exportOrders: async (params = {}) => {
    return await apiClient.get('/api/admin/export/orders', { 
      params,
      responseType: 'blob'
    });
  },
};

export default apiClient;
